import { useEffect, useState } from "react";
import { OrderBoxResponseItem } from "./OrderBoxResponseItem";
import styled from "styled-components";
import { OrderBoxToggleButton } from "./OrderBoxToggleButton";
import { OrderItem } from "src/contexts/documentationArea/domain/OrderItem";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { OrderResponseStatus } from "src/contexts/documentationArea/domain/OrderResponseStatus";

import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { colors } from "@permisso/permisso-components";

const MAX_SCROLLABLE_HEIGHT = "13.75rem";

interface OrderBoxResponsesListProps {
  order: OrderItem;
}

export const OrderBoxResponsesList = ({ order }: OrderBoxResponsesListProps) => {
  const [showAll, setShowAll] = useState(false);
  const { isMobile } = useIsMedia();
  const { selectedActorId } = useDocumentationArea();
  const DEFAULT_VISIBLE_COUNT = isMobile ? 3 : 1;

  const isShared = order.isOtherDocument() && order.getRequest().isShared;

  /**
   * Sort responses by status first: valid, then pending_review, then invalid.
   * Within each status group, sort by createdAt (newest first).
   */
  const sortedResponses = order.getResponses().sort((a, b) => {
    const statusOrder = {
      [OrderResponseStatus.VALID]: 0,
      [OrderResponseStatus.PENDING_REVIEW]: 1,
      [OrderResponseStatus.INVALID]: 2,
    };
    const statusComparison = statusOrder[a.getStatus()] - statusOrder[b.getStatus()];

    if (statusComparison === 0) {
      return b.getCreatedAt().getTime() - a.getCreatedAt().getTime();
    }

    return statusComparison;
  });

  const visibleResponses = isMobile
    ? showAll
      ? sortedResponses
      : sortedResponses.slice(0, DEFAULT_VISIBLE_COUNT)
    : sortedResponses;

  const hasMoreResponses = sortedResponses.length > DEFAULT_VISIBLE_COUNT;

  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  useEffect(() => {
    if (showAll) {
      setShowAll(false);
    }
  }, [order.validResponses().length]);

  useEffect(() => {
    if (order.isProcessing()) {
      setShowAll(false);
    }
  }, [order.isProcessing()]);

  const responsesToShow = isMobile ? visibleResponses : sortedResponses;

  if (order.isProcessing()) return null;

  return (
    <Container>
      <ResponsesContainer
        $isMobile={isMobile}
        $isExpanded={showAll}
        $hasItems={sortedResponses.length > 0}
      >
        {responsesToShow.map((response, index) => (
          <OrderBoxResponseItem
            key={index}
            response={response}
            orderType={order.getType()}
            $isHidden={!isMobile && !showAll && index > 0}
            isShared={isShared}
            currentActorId={selectedActorId}
          />
        ))}
      </ResponsesContainer>
      {hasMoreResponses && (
        <OrderBoxToggleButton
          isFileListExpanded={showAll}
          missingResponsesCount={sortedResponses.length - DEFAULT_VISIBLE_COUNT}
          handleToggle={toggleShowAll}
        />
      )}
    </Container>
  );
};
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.5rem;
`;

const ResponsesContainer = styled.div<{
  $isMobile: boolean;
  $isExpanded: boolean;
  $hasItems: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${({ $isMobile, $isExpanded, $hasItems }) => {
    const isDesktopWithItems = !$isMobile && $hasItems;
    if (isDesktopWithItems) {
      return `
        max-height: ${$isExpanded ? MAX_SCROLLABLE_HEIGHT : "auto"};
        overflow-y: ${$isExpanded ? "auto" : "hidden"};
        transition: max-height 0.3s ease;
        
        &::-webkit-scrollbar {
          width: .375rem;
        }
        
        &::-webkit-scrollbar-track {
          background: ${colors.grey300};
          border-radius: .1875rem;
        }
        
        &::-webkit-scrollbar-thumb {
          background: ${colors.grey500};
          border-radius: .1875rem;
        }
        
        &::-webkit-scrollbar-thumb:hover {
          background: ${colors.grey700};
        }
      `;
    }
    return "";
  }}
`;
